/**
 * The acceleration trace — a car's road test, as a curve (SPEC-004 PR-4.5).
 *
 * The catalogue carries two to four MEASURED points per car: a speed the test
 * reached and the time it took to get there, all from one test of one car on one
 * day (`carSpecs.js` states that rule and enforces it by authorship). This module
 * turns those points into something drawable, and the whole of its design is
 * about the difference between the two.
 *
 * A measured point is a fact. Everything between two measured points is an
 * INTERPOLATION, and an interpolation drawn with the same weight as a fact is
 * how a chart lies. So this module answers with both, separately labelled, and
 * the component draws them differently — the points as marks, the curve as a
 * thinner line that is explicitly not claiming to have been measured.
 *
 * It returns DATA-SPACE numbers: seconds and km/h, the units the catalogue
 * stores. Mapping those onto a viewBox is the chart's job, because the mapping
 * needs to know how wide the chart is and this module must not.
 *
 * Pure, and deliberately free of React, the DOM and `t()` — the same argument
 * `carProperties.js` makes for itself, for the same reason.
 */

/**
 * How finely the curve between measured points is sampled.
 *
 * Enough that the line reads as a curve at phone width and no more: this array
 * becomes an SVG `d` attribute, and every extra sample is bytes in the DOM for
 * a bend nobody can see. Twelve segments between points is roughly a sample
 * every three or four CSS pixels on the widest span a phone draws.
 */
const SAMPLES_PER_SEGMENT = 12;

/** The line every trace starts from, and the one point no test has to publish. */
const ORIGIN = { kmh: 0, seconds: 0 };

/** Does this car have a road test to draw? */
export function hasAccelerationTrace(car) {
    return Boolean(car?.specs?.acceleration?.points?.length);
}

/**
 * The slopes at each point, chosen so the curve through them cannot overshoot.
 *
 * This is the Fritsch–Carlson filter on a cubic Hermite spline, and the reason
 * it is here rather than a plain cubic is the honesty argument in this module's
 * header. An ordinary spline through four points OVERSHOOTS between them: it
 * would draw the Corvette passing 250 km/h somewhere between its measured 179
 * and its measured 241 and coming back down, which is a specific false claim
 * about a car, invented by an algorithm, on a graph whose entire proposition is
 * that these are real figures.
 *
 * A monotone filter cannot do that. The curve it draws is bounded at every point
 * by the two measurements on either side of it, so the strongest thing the
 * interpolation can be is WRONG about the shape — never wrong about the range.
 * That is the most a curve through four points is entitled to claim.
 */
function monotoneSlopes(points) {
    const secants = [];

    for (let index = 0; index < points.length - 1; index += 1) {
        const runSeconds = points[index + 1].seconds - points[index].seconds;

        secants.push((points[index + 1].kmh - points[index].kmh) / runSeconds);
    }

    const slopes = points.map((point, index) => {
        if (index === 0) {
            return secants[0];
        }

        if (index === points.length - 1) {
            return secants[secants.length - 1];
        }

        // The average of the two secants meeting here, which is the ordinary
        // Hermite choice; the clamp below is what makes it monotone.
        return (secants[index - 1] + secants[index]) / 2;
    });

    for (let index = 0; index < secants.length; index += 1) {
        const secant = secants[index];
        // A flat secant means two points at the same speed, which the content
        // test forbids — but a zero here would divide badly, so it is handled
        // rather than assumed away.
        const alpha = secant === 0 ? 0 : slopes[index] / secant;
        const beta = secant === 0 ? 0 : slopes[index + 1] / secant;
        const magnitude = Math.hypot(alpha, beta);

        if (magnitude > 3) {
            slopes[index] = (3 / magnitude) * alpha * secant;
            slopes[index + 1] = (3 / magnitude) * beta * secant;
        }
    }

    return slopes;
}

/** One cubic Hermite segment, sampled. */
function sampleSegment(from, to, slopeFrom, slopeTo, samples) {
    const span = to.seconds - from.seconds;
    const sampled = [];

    for (let step = 1; step <= samples; step += 1) {
        const t = step / samples;
        const t2 = t * t;
        const t3 = t2 * t;
        const h00 = 2 * t3 - 3 * t2 + 1;
        const h10 = t3 - 2 * t2 + t;
        const h01 = -2 * t3 + 3 * t2;
        const h11 = t3 - t2;

        sampled.push({
            seconds: from.seconds + t * span,
            kmh: h00 * from.kmh + h10 * span * slopeFrom + h01 * to.kmh + h11 * span * slopeTo,
        });
    }

    return sampled;
}

/**
 * A car's road test, ready to draw.
 *
 * `measured` are the points the test published, and they are what the chart
 * marks. `curve` is the interpolation through them INCLUDING the origin, and it
 * is what the chart strokes. `topSpeedKmh` is neither: no test in this catalogue
 * measured the time to reach a top speed, so it is returned separately for the
 * chart to draw as a ceiling the trace runs under rather than as the point the
 * trace arrives at. Drawing it as an endpoint would be inventing the one number
 * the whole graph is missing.
 *
 * Returns null rather than an empty shape for a car with no test, so a caller
 * that forgets to check renders nothing instead of an empty pair of axes — an
 * empty chart reads as "this car did not accelerate", which is worse than the
 * absence of a chart.
 */
export function accelerationTrace(car) {
    if (!hasAccelerationTrace(car)) {
        return null;
    }

    const { basis, points, source } = car.specs.acceleration;
    // The origin joins the points for the CURVE and not for the marks: the car
    // was genuinely at rest at zero, so the curve is entitled to start there,
    // but it is not a figure anybody published and it is not marked as one.
    const withOrigin = [ORIGIN, ...points];
    const slopes = monotoneSlopes(withOrigin);
    const curve = [ORIGIN];

    for (let index = 0; index < withOrigin.length - 1; index += 1) {
        curve.push(...sampleSegment(
            withOrigin[index],
            withOrigin[index + 1],
            slopes[index],
            slopes[index + 1],
            SAMPLES_PER_SEGMENT,
        ));
    }

    const lastMeasured = points[points.length - 1];

    return {
        source,
        basis,
        measured: points,
        curve,
        topSpeedKmh: car.specs.topSpeed?.kmh ?? null,
        // The axes are the measurements' own extent, not the top speed's. A
        // chart scaled to a 324 km/h ceiling would draw the F40's two real
        // points in the bottom-left corner and spend two thirds of its width on
        // a stretch of curve nobody recorded.
        duration: lastMeasured.seconds,
        ceiling: lastMeasured.kmh,
    };
}

/**
 * Where to put the gridlines, given an axis that runs from zero to `maximum`.
 *
 * A round step rather than `maximum / count`: the C64 dashboards this band is
 * drawn after label their axes 0, 10, 20, 30 — never 0, 7.4, 14.8 — and a reader
 * uses a gridline to estimate a value, which they can only do against a number
 * they can hold in their head.
 *
 * Returns the ticks WITHOUT the zero, which every axis draws as its own baseline
 * and which would otherwise be labelled twice at the corner.
 */
export function axisTicks(maximum, { steps = [1, 2, 5, 10, 20, 25, 50, 100], target = 4 } = {}) {
    if (!(maximum > 0)) {
        return [];
    }

    // The step whose tick COUNT lands nearest the target, rather than the first
    // step wider than `maximum / target`. The two differ exactly where it shows:
    // the Corvette's axis runs to 241, the arithmetic asks for a step of 60, and
    // "first step at least 60" is 100 — which draws that car two gridlines where
    // every other car in the deck gets four, on the one chart in the pack whose
    // shape most needs them.
    const step = steps.reduce((best, candidate) => {
        const distance = Math.abs(Math.floor(maximum / candidate) - target);
        const bestDistance = Math.abs(Math.floor(maximum / best) - target);

        return distance < bestDistance ? candidate : best;
    }, steps[0]);
    const ticks = [];

    for (let value = step; value <= maximum; value += step) {
        ticks.push(value);
    }

    return ticks;
}

/**
 * The figure the rank is taken on, at the precision the catalogue publishes it.
 *
 * ROUNDED, and the rounding is the whole subtlety. The Yellowbird's 0-100 is
 * RUF's own 3.65 s and the 959's is Porsche's 3.7, so on the raw numbers the
 * Yellowbird is quicker and would be ranked ahead. But the plate above this
 * chart prints both at one decimal, where both read 3.7 — so a rank taken on the
 * raw figures puts one car ahead of another while the screen shows the two of
 * them holding identical numbers, which reads as a bug in the ranking and is
 * indistinguishable from one.
 *
 * `Math.round(value * 10) / 10` rather than `toFixed(1)`, which is not the same
 * function: `(3.65).toFixed(1)` is `"3.6"` because 3.65 is stored a hair below
 * itself, while `Intl.NumberFormat` — which is what the plate actually uses —
 * answers `3.7`. Arithmetic rather than the formatter, because a rank is a fact
 * about the catalogue and must not change with the language on screen.
 */
function rankedSeconds(car) {
    const seconds = car?.specs?.zeroToHundred?.seconds;

    return Number.isFinite(seconds) ? Math.round(seconds * 10) / 10 : null;
}

/**
 * Where this car sits among the legends on 0-100 km/h.
 *
 * The rank is taken on the PUBLISHED 0-100, not on the road test this band
 * draws. That is deliberate and it is the only basis available: the traces come
 * from eleven different magazines and three cars have none at all, so a rank
 * computed from them would compare a Road & Track morning against an auto motor
 * und sport afternoon and would silently exclude a fifth of the catalogue. The
 * published figure is one basis, stated by each marque, and every one of the
 * twenty-four has it.
 *
 * Competition ranking, so equal figures share a place and the next one is
 * skipped — two cars at 3.7 s are both first and the next car is third. Both
 * ties in the shipped pack are real: the Yellowbird against the 959 at 3.7, and
 * the 993 against the Esprit at 5.6.
 *
 * Null unless the car is IN the field it is being ranked against. A rank of
 * "3rd of 13" computed for a car the list does not contain is arithmetic nobody
 * asked for, and it is what a filtered shelf would otherwise produce.
 */
export function accelerationRank(car, cars) {
    const mine = rankedSeconds(car);
    const field = (Array.isArray(cars) ? cars : []).filter((entry) => rankedSeconds(entry) !== null);
    const present = field.some((entry) => entry.id === car?.id);

    if (mine === null || !present) {
        return null;
    }

    return {
        position: field.filter((entry) => rankedSeconds(entry) < mine).length + 1,
        total: field.length,
    };
}
